import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChartLine, LockSimple, ArrowSquareOut, CheckCircle } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { IndicatorNotice } from "@/components/PriceCards";

export const GraduatesBadge = () => (
  <span className="inline-flex items-center gap-1 rounded-full border border-[#E0A21B] bg-[#E0A21B]/15 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-[#E0A21B]" data-testid="graduates-only-badge">
    <LockSimple size={12} weight="bold" /> Lecture graduates only
  </span>
);

export const GraduatesNotice = ({ dark = false, className = "" }) => (
  <div className={`rounded-md border-2 border-[#E0A21B] p-4 ${dark ? "bg-[#0F2342] text-white" : "bg-[#FFF8E6] text-[#0B1B33]"} ${className}`} data-testid="graduates-notice">
    <p className="flex items-center gap-2 text-base font-extrabold"><LockSimple size={20} weight="bold" className="shrink-0 text-[#E0A21B]" /> Available exclusively to lecture graduates.</p>
    <p className={`mt-2 text-sm font-semibold ${dark ? "text-white/85" : "text-[#334155]"}`}>
      The Mission Control Indicator is only available to people who have attended a full-day Mission Control Trading lecture. Reserve your seat to unlock access.
    </p>
    <Link to="/cities" className="btn-gold mt-3 inline-flex" data-testid="graduates-notice-find-class-btn">Find a class in your city →</Link>
  </div>
);

const GraduateBuy = ({ d, reload }) => {
  const [u, setU] = useState(d.tradingview_username || "");
  const send = async () => {
    try { await api.post("/me/indicator/request", { username: u }); toast.success("Thanks. We'll add your TradingView username for access."); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3" data-testid="indicator-graduate-panel">
      <p className="flex items-center gap-2 text-sm font-semibold text-[#1E8E5A]"><CheckCircle weight="fill" /> You are a lecture graduate. You can buy the indicator.</p>
      {d.purchase_url
        ? <a href={d.purchase_url} target="_blank" rel="noreferrer" className="btn-gold inline-flex" data-testid="indicator-buy-btn">Buy the indicator on TradingView <ArrowSquareOut /></a>
        : <button className="btn-gold opacity-60" disabled data-testid="indicator-buy-btn">Purchase link coming soon</button>}
      <label className="block text-sm">After you buy, enter your TradingView username so we can grant access
        <div className="mt-1 flex gap-2">
          <Input placeholder="e.g. trader_jane" value={u} onChange={(e) => setU(e.target.value)} data-testid="indicator-username-input" />
          <button className="btn-outline" onClick={send} data-testid="indicator-username-submit-btn">Submit</button>
        </div>
      </label>
      {d.request && <p className="text-xs text-[#475569]" data-testid="indicator-request-status">Username {d.request.username}: {d.request.status === "granted" ? "access granted" : "waiting for access to be added"}</p>}
    </div>
  );
};

export const IndicatorAccess = ({ dark = false }) => {
  const { user } = useAuth();
  const { data: d, error, reload } = useFetch(user ? "/me/indicator" : null);
  if (user && !d && !error) return null;
  if (d?.eligible) return <GraduateBuy d={d} reload={reload} />;
  return (
    <div className="space-y-3" data-testid="indicator-locked">
      <GraduatesNotice dark={dark} />
      <button className="inline-flex cursor-not-allowed items-center gap-2 rounded-md border border-[#94A3B8] bg-[#E2E8F0] px-4 py-2.5 text-sm font-semibold text-[#475569]" disabled data-testid="indicator-buy-btn-locked"><LockSimple /> Buy the indicator (graduates only)</button>
    </div>
  );
};

export const IndicatorCard = () => (
  <div className="panel space-y-3 p-5" data-testid="indicator-card">
    <h3 className="flex items-center gap-2 font-display text-xl font-bold uppercase"><ChartLine className="text-[#E0A21B]" /> Mission Control indicator</h3>
    <IndicatorNotice testid="indicator-notice-profile" />
    <IndicatorAccess />
  </div>
);
